import { isObservable } from 'rxjs';
import { DataTableSource } from './data-source.types';
import { DataSourceAdapter } from './data-source-adapter.interface';
import { StaticDataSource } from './static-data-source';
import { ObservableDataSource } from './observable-data-source';
import { PaginatedDataService } from '../../../services/paginated-data.service';
import { PaginatedDataSourceAdapter } from './paginated-data-source-adapter';

/**
 * Resolves a DataTableSource input into the appropriate adapter.
 * Called internally by DataTableComponent on initialization.
 *
 * ## Resolution Order
 * 1. `T[]` → StaticDataSource
 * 2. `Observable<T[]>` → ObservableDataSource
 * 3. `PaginatedDataService` → PaginatedDataSourceAdapter
 * 4. `DataSourceAdapter` → returned as-is (custom adapter)
 *
 * @example
 * ```ts
 * // These are all valid:
 * resolveDataSource([{ id: 1 }]);              // → StaticDataSource
 * resolveDataSource(of([{ id: 1 }]));          // → ObservableDataSource
 * resolveDataSource(myPaginatedService);       // → PaginatedDataSourceAdapter
 * resolveDataSource(myCustomAdapter);          // → myCustomAdapter
 * ```
 *
 * @param source - The dataSource input from the component
 * @returns Appropriate adapter instance
 * @throws Error if source type cannot be determined
 */
export function resolveDataSource<T>(source: DataTableSource<T>): DataSourceAdapter<T> {
  if (Array.isArray(source)) {
    return new StaticDataSource(source);
  }

  if (isObservable(source)) {
    return new ObservableDataSource(source);
  }

  if (isPaginatedDataService<T>(source)) {
    return new PaginatedDataSourceAdapter<T>(source);
  }

  if (isDataSourceAdapter(source)) {
    return source;
  }

  throw new Error(`Unknown dataSource type: ${typeof source}`);
}

/**
 * Type guard for DataSourceAdapter.
 * Checks for required `connect`, `disconnect` methods and `type` property.
 */
export function isDataSourceAdapter<T>(source: any): source is DataSourceAdapter<T> {
  return (
    source &&
    typeof source.connect === 'function' &&
    typeof source.disconnect === 'function' &&
    'type' in source
  );
}

/**
 * Type guard for PaginatedDataService.
 * Checks for `data$`, `totalCount$`, `search`, and `changePage`.
 */
export function isPaginatedDataService<T>(source: any): source is PaginatedDataService<T, any> {
  return (
    source &&
    'data$' in source &&
    'totalCount$' in source &&
    typeof source.search === 'function' &&
    typeof source.changePage === 'function'
  );
}
